import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, Tooltip, Tabs, Tab, InputAdornment, TablePagination,
  Divider, Dialog, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalance as LedgersIcon, Person as PersonIcon, Folder as AccountIcon,
  Search as SearchIcon, Undo as ReverseIcon, History as HistoryIcon,
  Verified as AuditIcon, GetApp as DownloadIcon, Visibility as ViewIcon,
  Refresh as RefreshIcon, PendingActions as PendingIcon,
  Check as CheckIcon, Close as RejectIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';
import ReceiptDialog from '../payments/ReceiptDialog';
import OtherReceiptDialog from '../payments/OtherReceiptDialog';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
};
const today    = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);

function statusChip(status) {
  switch (status) {
    case 'POSTED':   return { color: 'success', label: 'POSTED' };
    case 'APPROVED': return { color: 'success', label: 'APPROVED' };
    case 'PENDING':  return { color: 'warning', label: 'PENDING' };
    case 'REVERSED': return { color: 'error',   label: 'REVERSED' };
    case 'VOIDED':   return { color: 'default', label: 'VOIDED' };
    case 'REJECTED': return { color: 'error',   label: 'REJECTED' };
    default:         return { color: 'default', label: status || '—' };
  }
}

function exportCSV(rows, header, filename) {
  if (!rows.length) return;
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header.map(h => h.label), ...rows.map(r => header.map(h => h.get(r)))]
    .map(line => line.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Student picker (left pane) — Active / Alumni / Leavers ──────
const STUDENT_TABS = [
  { v: 'active', label: 'Active',  color: 'success' },
  { v: 'alumni', label: 'Alumni',  color: 'secondary' },
  { v: 'leaver', label: 'Leavers', color: 'warning' },
];

function StudentPicker({ picked, onPick }) {
  const [statusTab, setStatusTab] = useState('active');
  const [students, setStudents]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [filterCla, setFCla]      = useState('');
  const [classes, setClasses]     = useState([]);
  const [counts, setCounts]       = useState({ active: 0, alumni: 0, leaver: 0 });

  useEffect(() => {
    API.get('/classes').then(r => setClasses((r.data || []).map(c => c.class).filter(Boolean))).catch(() => {});
    API.get('/admissions/counts')
      .then(r => setCounts({
        active: Number(r.data?.active || 0),
        alumni: Number(r.data?.alumni || 0),
        leaver: Number(r.data?.leaver || 0),
      })).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: statusTab, perPage: 500 };
      if (filterCla) params.cla = filterCla;
      if (search)    params.q = search;
      const r = await API.get('/admissions', { params });
      setStudents(r.data?.rows || []);
    } catch { setStudents([]); }
    finally { setLoading(false); }
  };
  // Reload on status / class change; debounce on search.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusTab, filterCla]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  const tabColor = (v) => STUDENT_TABS.find(t => t.v === v)?.color || 'default';

  return (
    <Card sx={{ alignSelf: 'flex-start' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <PersonIcon color="primary" />
          <Typography fontWeight={800}>Students</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton></span>
          </Tooltip>
        </Stack>

        {/* Status tabs with counts */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.2 }}>
          <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)}
            variant="fullWidth"
            sx={{ minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, fontWeight: 700, textTransform: 'none', fontSize: 12 } }}>
            {STUDENT_TABS.map(t => (
              <Tab key={t.v} value={t.v}
                label={
                  <Stack direction="row" spacing={0.7} alignItems="center">
                    <span>{t.label}</span>
                    <Chip size="small" color={t.color} label={counts[t.v] ?? 0}
                      sx={{ height: 16, fontSize: 9, fontWeight: 800,
                            '& .MuiChip-label': { px: 0.6 } }} />
                  </Stack>
                } />
            ))}
          </Tabs>
        </Box>

        <Stack spacing={1} sx={{ mb: 1 }}>
          <TextField fullWidth size="small" placeholder="Search name or index"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Class" value={filterCla}
            onChange={(e) => setFCla(e.target.value)}>
            <MenuItem value=""><em>All classes</em></MenuItem>
            {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Stack>

        <Box sx={{ maxHeight: 540, overflowY: 'auto', pr: 0.5 }}>
          {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {!loading && students.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No students.
            </Typography>
          )}
          {!loading && students.map(s => {
            const isPicked = picked?.index_no === s.index_no;
            return (
              <Paper key={`${s._source || statusTab}-${s.trans_code}`} variant="outlined"
                onClick={() => onPick({
                  index_no:  s.index_no,
                  firstname: s.firstname,
                  lastname:  s.lastname,
                  calss:     s.calss,
                  type:      s.type,
                  status:    statusTab,
                })}
                sx={{
                  px: 1.2, py: 0.9, mb: 0.6, cursor: 'pointer',
                  borderColor: isPicked ? 'primary.main' : 'divider',
                  borderWidth: isPicked ? 2 : 1,
                  bgcolor: isPicked ? (t) => alpha(t.palette.primary.main, 0.08) : 'background.paper',
                  '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
                }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700,
                    bgcolor: statusTab === 'leaver' ? 'grey.500'
                          : statusTab === 'alumni' ? 'secondary.main' : 'primary.main' }}>
                    {(s.firstname?.[0] || '?').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" fontWeight={isPicked ? 800 : 700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {s.firstname} {s.lastname}
                      </Typography>
                      <Chip size="small"
                        label={STUDENT_TABS.find(t => t.v === statusTab).label}
                        color={tabColor(statusTab)}
                        variant="outlined"
                        sx={{ height: 16, fontSize: 9, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={TNUM} noWrap>
                      {s.index_no} · {s.calss || '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── 1. Student Ledger ─────────────────────────────────────────
function StudentLedgerView({ openReceipt, openReverse, canReverse, reloadVersion = 0 }) {
  const [picked, setPicked] = useState(null);
  const [filters, setFilters] = useState({
    from: monthAgo(), to: today(), term: '', acc_yer: '', account: '', status: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  // The account dropdown is built from whatever accounts the student
  // actually has on file, so we load WITHOUT the account filter and apply
  // it client-side. That keeps the dropdown stable as the user toggles
  // accounts without round-tripping the server.
  const load = async (s = picked, f = filters) => {
    if (!s) { setData(null); return; }
    setLoading(true);
    try {
      const params = { ...f };
      delete params.account; // applied client-side below
      const r = await API.get(`/ledgers/students/${encodeURIComponent(s.index_no)}`, { params });
      setData(r.data);
      setPage(0);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load ledger')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (picked) load(picked); /* eslint-disable-next-line */ }, [picked, reloadVersion]);

  const allEntries = data?.entries || [];

  // Distinct accounts on the student's record, with a per-account row count
  // so the dropdown can show "school_fees · 12" as a hint.
  const accountOptions = useMemo(() => {
    const counts = new Map();
    for (const r of allEntries) {
      const a = (r.account || '').trim();
      if (!a) continue;
      counts.set(a, (counts.get(a) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([account, n]) => ({ account, n }))
      .sort((a, b) => a.account.localeCompare(b.account));
  }, [allEntries]);

  // Filtered view — applies the account picker client-side.
  const entries = useMemo(
    () => filters.account
      ? allEntries.filter(r => (r.account || '').trim() === filters.account)
      : allEntries,
    [allEntries, filters.account]
  );
  const paged = entries.slice(page * perPage, page * perPage + perPage);
  const totals = useMemo(() => {
    let debit = 0, credit = 0, adj = 0;
    for (const r of entries) {
      if (r.status === 'REVERSED') continue;
      debit  += Number(r.debit  || 0);
      credit += Number(r.credit || 0);
      adj    += Number(r.adjustment || 0);
    }
    return { debit, credit, adj, balance: debit + adj - credit };
  }, [entries]);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      {/* LEFT — Student picker with status tabs */}
      <StudentPicker picked={picked} onPick={setPicked} />

      {/* RIGHT — Ledger entries */}
      <Box>
        {!picked && (
          <Alert severity="info" variant="outlined">
            Pick a student from the left to load their full transaction ledger.
          </Alert>
        )}

        {picked && (
          <>
            {/* Student summary + filter bar */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {(picked.firstname?.[0] || '?').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={800} noWrap>
                      {picked.firstname} {picked.lastname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {picked.index_no} · {picked.calss || '—'} · {picked.type || '—'}
                      {picked.status && (
                        <Chip size="small"
                          label={STUDENT_TABS.find(t => t.v === picked.status)?.label || picked.status}
                          color={STUDENT_TABS.find(t => t.v === picked.status)?.color || 'default'}
                          variant="outlined"
                          sx={{ ml: 1, height: 16, fontSize: 9, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }} />
                      )}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9, display: 'block', lineHeight: 1 }}>
                        Total Paid
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: 'success.dark', ...TNUM, fontSize: 14, lineHeight: 1.2 }}>
                        GHS {fmtMoney(totals.credit)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9, display: 'block', lineHeight: 1 }}>
                        Net (period)
                      </Typography>
                      <Typography sx={{ fontWeight: 800, ...TNUM, fontSize: 14, lineHeight: 1.2,
                        color: totals.balance > 0 ? 'error.main' : 'text.disabled' }}>
                        GHS {fmtMoney(totals.balance)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Tooltip title="Export CSV">
                    <span><IconButton onClick={() => exportCSV(entries, [
                      { label: 'Date',         get: (r) => fmtDate(r.entry_date) },
                      { label: 'Receipt',      get: (r) => r.receipt_no || '' },
                      { label: 'Account',      get: (r) => r.account || '' },
                      { label: 'Description',  get: (r) => r.description || '' },
                      { label: 'Debit',        get: (r) => r.debit || 0 },
                      { label: 'Credit',       get: (r) => r.credit || 0 },
                      { label: 'Adjustment',   get: (r) => r.adjustment || 0 },
                      { label: 'Balance',      get: (r) => r.balance || 0 },
                      { label: 'Status',       get: (r) => r.status || '' },
                    ], `Student-${picked.index_no}.csv`)}><DownloadIcon /></IconButton></span>
                  </Tooltip>
                </Stack>

                <Divider sx={{ mb: 1.2 }} />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems="center">
                  <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                    value={filters.from} onChange={(e) => setF('from', e.target.value)} sx={{ flex: 1 }} />
                  <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                    value={filters.to} onChange={(e) => setF('to', e.target.value)} sx={{ flex: 1 }} />
                  <TextField select size="small" label="Account"
                    value={filters.account}
                    onChange={(e) => setF('account', e.target.value)}
                    sx={{ flex: 1 }}
                    helperText={accountOptions.length === 0
                      ? (loading ? 'Loading…' : 'No accounts on this student')
                      : `${accountOptions.length} account${accountOptions.length === 1 ? '' : 's'}`}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v) => v
                        ? v
                        : (<em style={{ color: '#888' }}>All accounts</em>),
                    }}>
                    <MenuItem value=""><em>All accounts</em></MenuItem>
                    {accountOptions.map(({ account, n }) => (
                      <MenuItem key={account} value={account}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                          <Box sx={{ flex: 1, ...TNUM }}>{account}</Box>
                          <Chip size="small" label={n}
                            sx={{ height: 18, fontSize: 10, fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }} />
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Status" value={filters.status}
                    onChange={(e) => setF('status', e.target.value)} sx={{ flex: 1 }}>
                    <MenuItem value="">All</MenuItem>
                    {['POSTED', 'PENDING', 'REVERSED', 'REJECTED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <Button variant="contained" onClick={() => load()}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>Apply</Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Ledger entries table */}
            <Card>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Receipt</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Adj.</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
                    {!loading && entries.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No entries.</TableCell></TableRow>}
                    {!loading && paged.map((r, i) => {
                      const sc = statusChip(r.status);
                      const isReversed = r.status === 'REVERSED';
                      return (
                        <TableRow key={i} hover sx={{
                          '& td': { fontSize: 12, py: 0.6, ...TNUM, whiteSpace: 'nowrap' },
                          opacity: isReversed ? 0.55 : 1,
                        }}>
                          <TableCell>{fmtDate(r.entry_date)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no || '—'}</TableCell>
                          <TableCell>{r.account || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={r.description || ''}><span>{r.description || '—'}</span></Tooltip>
                          </TableCell>
                          <TableCell align="right">{r.debit ? `GHS ${fmtMoney(r.debit)}` : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.dark' }}>
                            {r.credit ? `GHS ${fmtMoney(r.credit)}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'warning.dark' }}>
                            {r.adjustment ? `GHS ${fmtMoney(r.adjustment)}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {r.balance != null ? `GHS ${fmtMoney(r.balance)}` : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" color={sc.color} label={sc.label} sx={{ fontWeight: 800, height: 20 }} />
                          </TableCell>
                          <TableCell align="right">
                            {r.kind === 'PAYMENT' && (
                              <Tooltip title="View receipt">
                                <IconButton size="small" color="primary" onClick={() => openReceipt(r)}><ViewIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                            {r.kind === 'PAYMENT' && r.status === 'POSTED' && r.payment_id && canReverse && (
                              <Tooltip title="Reverse">
                                <IconButton size="small" color="error" onClick={() => openReverse(r)}><ReverseIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={entries.length}
                page={page} onPageChange={(_, p) => setPage(p)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows:" showFirstButton showLastButton />
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Account picker (left pane) ─────────────────────────────────
function AccountPicker({ picked, onPick }) {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/accounts');
      setAccounts(r.data || []);
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(a => (a.account || '').toLowerCase().includes(q));
  }, [accounts, search]);

  return (
    <Card sx={{ alignSelf: 'flex-start' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AccountIcon color="primary" />
          <Typography fontWeight={800}>Accounts</Typography>
          <Chip size="small" label={accounts.length} sx={{ fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton></span>
          </Tooltip>
        </Stack>

        <TextField fullWidth size="small" placeholder="Search account name"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

        <Box sx={{ maxHeight: 540, overflowY: 'auto', pr: 0.5 }}>
          {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {!loading && filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No accounts.
            </Typography>
          )}
          {!loading && filtered.map(a => {
            const isPicked = picked === a.account;
            return (
              <Paper key={a.account} variant="outlined"
                onClick={() => onPick(a.account)}
                sx={{
                  px: 1.2, py: 0.9, mb: 0.6, cursor: 'pointer',
                  borderColor: isPicked ? 'primary.main' : 'divider',
                  borderWidth: isPicked ? 2 : 1,
                  bgcolor: isPicked ? (t) => alpha(t.palette.primary.main, 0.08) : 'background.paper',
                  '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
                }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Avatar sx={{ width: 30, height: 30, fontSize: 13, fontWeight: 700, bgcolor: 'primary.main' }}>
                    {(a.account?.[0] || '?').toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" fontWeight={isPicked ? 800 : 700} noWrap sx={{ ...TNUM, flex: 1, minWidth: 0 }}>
                    {a.account}
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── 2. Account Ledger ─────────────────────────────────────────
function AccountLedgerView({ openReceipt, openReverse, canReverse, reloadVersion = 0 }) {
  const [picked, setPicked] = useState('');
  const [filters, setFilters] = useState({
    from: monthAgo(), to: today(), cla: '', status: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const load = async (acc = picked, f = filters) => {
    if (!acc) { setData(null); return; }
    setLoading(true);
    try {
      const r = await API.get(`/ledgers/accounts/${encodeURIComponent(acc)}`, { params: f });
      setData(r.data);
      setPage(0);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load account ledger')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (picked) load(picked); /* eslint-disable-next-line */ }, [picked, reloadVersion]);

  const accEntries = data?.entries || [];
  const accPaged   = accEntries.slice(page * perPage, page * perPage + perPage);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      {/* LEFT — Account picker */}
      <AccountPicker picked={picked} onPick={setPicked} />

      {/* RIGHT — Filters + entries table */}
      <Box>
        {!picked && (
          <Alert severity="info" variant="outlined">
            Pick an account from the left to load its ledger.
          </Alert>
        )}

        {picked && (
          <>
            {/* Account summary + filter strip */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontWeight: 700 }}>
                    {(picked?.[0] || '?').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={800} noWrap sx={TNUM}>{picked}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Income type / fee account
                    </Typography>
                  </Box>
                  {data && (
                    <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9, display: 'block', lineHeight: 1 }}>
                          Entries
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, ...TNUM }}>
                          {Number(data.count || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9, display: 'block', lineHeight: 1 }}>
                          Total
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: 'success.dark', ...TNUM, fontSize: 14, lineHeight: 1.2 }}>
                          GHS {fmtMoney(data.total)}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  <Tooltip title="Export CSV">
                    <span>
                      <IconButton disabled={!data} onClick={() => exportCSV(data?.entries || [], [
                        { label: 'Date',     get: (r) => fmtDate(r.entry_date) },
                        { label: 'Receipt',  get: (r) => r.receipt_no || '' },
                        { label: 'Student',  get: (r) => r.payer_name || r.student_id || '' },
                        { label: 'Class',    get: (r) => r.class_name || '' },
                        { label: 'Term',     get: (r) => r.term || '' },
                        { label: 'Year',     get: (r) => r.acc_yer || '' },
                        { label: 'Amount',   get: (r) => r.amount || 0 },
                        { label: 'Mode',     get: (r) => r.mode || '' },
                        { label: 'Cashier',  get: (r) => r.received_by_name || '' },
                        { label: 'Status',   get: (r) => r.status || '' },
                      ], `Account-${picked}.csv`)}><DownloadIcon /></IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <Divider sx={{ mb: 1.2 }} />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems="center">
                  <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                    value={filters.from} onChange={(e) => setF('from', e.target.value)} sx={{ flex: 1 }} />
                  <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                    value={filters.to} onChange={(e) => setF('to', e.target.value)} sx={{ flex: 1 }} />
                  <TextField size="small" label="Class" value={filters.cla}
                    onChange={(e) => setF('cla', e.target.value)} sx={{ flex: 1 }} />
                  <TextField select size="small" label="Status" value={filters.status}
                    onChange={(e) => setF('status', e.target.value)} sx={{ flex: 1 }}>
                    <MenuItem value="">All</MenuItem>
                    {['POSTED', 'REVERSED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <Button variant="contained" onClick={() => load()}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>Apply</Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Entries table */}
            <Card>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Receipt</TableCell>
                      <TableCell>Student / Payer</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Running</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Cashier</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
                    {!loading && data && accEntries.length === 0 && (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No entries.</TableCell></TableRow>
                    )}
                    {!loading && data && accPaged.map((r, i) => {
                      const sc = statusChip(r.status);
                      const isReversed = r.status === 'REVERSED';
                      return (
                        <TableRow key={i} hover sx={{
                          '& td': { fontSize: 12, py: 0.6, ...TNUM, whiteSpace: 'nowrap' },
                          opacity: isReversed ? 0.55 : 1,
                        }}>
                          <TableCell>{fmtDate(r.entry_date)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>
                              {r.payer_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={TNUM}>
                              {r.student_id || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{r.class_name || '—'}</TableCell>
                          <TableCell>{r.term || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                            GHS {fmtMoney(r.amount)}
                          </TableCell>
                          <TableCell align="right" sx={TNUM}>GHS {fmtMoney(r.running_total)}</TableCell>
                          <TableCell>{r.mode || '—'}</TableCell>
                          <TableCell>{r.received_by_name || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" color={sc.color} label={sc.label} sx={{ fontWeight: 800, height: 20 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View receipt">
                              <IconButton size="small" color="primary" onClick={() => openReceipt(r)}><ViewIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            {r.status === 'POSTED' && (r.payment_id || r.transaction_id) && canReverse && (
                              <Tooltip title="Reverse">
                                <IconButton size="small" color="error" onClick={() => openReverse(r)}><ReverseIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={accEntries.length}
                page={page} onPageChange={(_, p) => setPage(p)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows:" showFirstButton showLastButton />
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Reusable left filter panel — same shape as Student/Account picker ──
function FilterPanel({ icon, title, badge, children, onApply, applyLabel = 'Apply', loading, footer }) {
  return (
    <Card sx={{ alignSelf: 'flex-start' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
          {icon}
          <Typography fontWeight={800}>{title}</Typography>
          <Box sx={{ flex: 1 }} />
          {badge}
        </Stack>
        <Stack spacing={1.2}>
          {children}
        </Stack>
        {onApply && (
          <Button fullWidth variant="contained" sx={{ mt: 1.5, fontWeight: 800, textTransform: 'none' }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={onApply}>
            {applyLabel}
          </Button>
        )}
        {footer && <Box sx={{ mt: 1.2 }}>{footer}</Box>}
      </CardContent>
    </Card>
  );
}

// Quick-pick date presets used by every left-panel filter card.
const DATE_PRESETS_LDG = [
  { label: 'Today',         range: () => ({ from: today(), to: today() }) },
  { label: 'Yesterday',     range: () => {
      const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      return { from: y, to: y };
    } },
  { label: 'This week',     range: () => {
      const d = new Date(); const day = (d.getDay() + 6) % 7;
      const f = new Date(d); f.setDate(d.getDate() - day);
      return { from: f.toISOString().slice(0, 10), to: today() };
    } },
  { label: 'This month',    range: () => {
      const d = new Date();
      return { from: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10), to: today() };
    } },
  { label: 'Last 30 days',  range: () => ({
      from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: today(),
    }) },
  { label: 'This year',     range: () => ({
      from: `${new Date().getFullYear()}-01-01`, to: today(),
    }) },
];

function DatePresetChips({ from, to, onChange }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
      {DATE_PRESETS_LDG.map(p => {
        const r = p.range();
        const active = r.from === from && r.to === to;
        return (
          <Chip key={p.label} size="small" label={p.label}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => onChange(r)}
            sx={{ fontWeight: 700, cursor: 'pointer', height: 22 }} />
        );
      })}
    </Stack>
  );
}

// ── 3. Transaction Search ─────────────────────────────────────
function TransactionSearchView({ openReceipt, openReverse, canReverse, reloadVersion = 0 }) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [hasSearched, setHasSearched] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/transactions/search', { params: { q, from, to, status } });
      setRows(r.data || []);
      setPage(0);
      setHasSearched(true);
    } catch (e) { swal.error(swal.pickError(e, 'Search failed')); }
    finally { setLoading(false); }
  };
  // After any reversal anywhere on the page, re-run the last search so
  // the just-reversed row flips its status and disappears from the
  // reversible set immediately.
  useEffect(() => {
    if (hasSearched) search();
    /* eslint-disable-next-line */
  }, [reloadVersion]);

  const paged = rows.slice(page * perPage, page * perPage + perPage);

  const total = useMemo(() =>
    rows.filter(r => r.status !== 'REVERSED')
        .reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      {/* LEFT — search filters */}
      <FilterPanel icon={<SearchIcon color="primary" />} title="Search" loading={loading}
        applyLabel="Search"
        onApply={search}
        badge={rows.length > 0 && (
          <Chip size="small" label={`${rows.length} hit${rows.length === 1 ? '' : 's'}`}
            color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        )}>
        <TextField fullWidth size="small" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="Receipt / student / txn / batch id"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <DatePresetChips from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        <Stack direction="row" spacing={1}>
          <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        <TextField select fullWidth size="small" label="Status" value={status}
          onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="">All statuses</MenuItem>
          {['POSTED', 'PENDING', 'REVERSED', 'REJECTED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {rows.length > 0 && (
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Hit total (excl. reversed)
            </Typography>
            <Typography fontWeight={800} color="success.dark" sx={{ ...TNUM, fontSize: 16 }}>
              GHS {fmtMoney(total)}
            </Typography>
          </Box>
        )}
      </FilterPanel>

      {/* RIGHT — results */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                <TableCell>Date</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Cashier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>Type a query in the left panel and press Enter.</TableCell></TableRow>}
              {!loading && paged.map((r, i) => {
                const sc = statusChip(r.status);
                const isReversed = r.status === 'REVERSED';
                return (
                  <TableRow key={i} hover sx={{
                    '& td': { fontSize: 12, py: 0.6, ...TNUM, whiteSpace: 'nowrap' },
                    opacity: isReversed ? 0.55 : 1,
                  }}>
                    <TableCell>{fmtDate(r.entry_date)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no}</TableCell>
                    <TableCell sx={TNUM}>{r.batch_id || '—'}</TableCell>
                    <TableCell>{r.payer_name || r.student_id}</TableCell>
                    <TableCell>{r.class_name || '—'}</TableCell>
                    <TableCell>{r.account || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      GHS {fmtMoney(r.amount)}
                    </TableCell>
                    <TableCell>{r.mode || '—'}</TableCell>
                    <TableCell>{r.received_by_name || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={sc.color} label={sc.label} sx={{ fontWeight: 800, height: 20 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View receipt">
                        <IconButton size="small" color="primary" onClick={() => openReceipt(r)}><ViewIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {r.status === 'POSTED' && (r.payment_id || r.transaction_id) && canReverse && (
                        <Tooltip title="Reverse">
                          <IconButton size="small" color="error" onClick={() => openReverse(r)}><ReverseIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={rows.length}
          page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:" showFirstButton showLastButton />
      </Card>
    </Box>
  );
}

// ── 4. Reversal Center ───────────────────────────────────────
// Same shape as Transaction Search but the left panel defaults to POSTED
// only and shows a clear warning so the operator stays focused on
// reversible rows.
function ReversalCenterView({ openReceipt, openReverse, canReverse, approvers = [], reloadVersion = 0 }) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState('POSTED');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [hasSearched, setHasSearched] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/transactions/search', { params: { q, from, to, status } });
      setRows(r.data || []);
      setPage(0);
      setHasSearched(true);
    } catch (e) { swal.error(swal.pickError(e, 'Search failed')); }
    finally { setLoading(false); }
  };
  // Re-run the last search after a reversal so the row that was just
  // reversed flips POSTED → REVERSED (and falls out of the POSTED filter).
  useEffect(() => {
    if (hasSearched) search();
    /* eslint-disable-next-line */
  }, [reloadVersion]);
  const paged = rows.slice(page * perPage, page * perPage + perPage);
  const total = useMemo(() =>
    rows.filter(r => r.status !== 'REVERSED')
        .reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      <FilterPanel icon={<ReverseIcon color="error" />} title="Find to reverse" loading={loading}
        applyLabel="Search"
        onApply={search}
        footer={
          <Stack spacing={1.2}>
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
              Reversals must be approved before they post. Each request is logged with the original transaction's universal id (UUID) so approvers can roll back exactly that transaction's rows — never another's.
            </Alert>
            <ApproversBar approvers={approvers} dense />
          </Stack>
        }>
        <TextField fullWidth size="small" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="Receipt / student / txn id"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <DatePresetChips from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        <Stack direction="row" spacing={1}>
          <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        <TextField select fullWidth size="small" label="Status" value={status}
          onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="POSTED">POSTED (reversible)</MenuItem>
          <MenuItem value="">All statuses</MenuItem>
          {['PENDING', 'REVERSED', 'REJECTED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {rows.length > 0 && (
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Hits · total (excl. reversed)
            </Typography>
            <Typography fontWeight={800} color="success.dark" sx={{ ...TNUM, fontSize: 16 }}>
              GHS {fmtMoney(total)}
            </Typography>
          </Box>
        )}
      </FilterPanel>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                <TableCell>Date</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Cashier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>Search a receipt or student in the left panel.</TableCell></TableRow>}
              {!loading && paged.map((r, i) => {
                const sc = statusChip(r.status);
                const isReversed = r.status === 'REVERSED';
                return (
                  <TableRow key={i} hover sx={{
                    '& td': { fontSize: 12, py: 0.6, ...TNUM, whiteSpace: 'nowrap' },
                    opacity: isReversed ? 0.55 : 1,
                  }}>
                    <TableCell>{fmtDate(r.entry_date)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no}</TableCell>
                    <TableCell sx={TNUM}>{r.batch_id || '—'}</TableCell>
                    <TableCell>{r.payer_name || r.student_id}</TableCell>
                    <TableCell>{r.class_name || '—'}</TableCell>
                    <TableCell>{r.account || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      GHS {fmtMoney(r.amount)}
                    </TableCell>
                    <TableCell>{r.mode || '—'}</TableCell>
                    <TableCell>{r.received_by_name || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={sc.color} label={sc.label} sx={{ fontWeight: 800, height: 20 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View receipt">
                        <IconButton size="small" color="primary" onClick={() => openReceipt(r)}><ViewIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {r.status === 'POSTED' && (r.payment_id || r.transaction_id) && canReverse && (
                        <Tooltip title="Reverse">
                          <Button size="small" variant="outlined" color="error"
                            startIcon={<ReverseIcon fontSize="small" />}
                            onClick={() => openReverse(r)}
                            sx={{ ml: 0.5, fontWeight: 700, textTransform: 'none', minWidth: 0, px: 1, py: 0.2 }}>
                            Reverse
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={rows.length}
          page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:" showFirstButton showLastButton />
      </Card>
    </Box>
  );
}

// ── 5. Reversal History ──────────────────────────────────────
function ReversalHistoryView({ reloadVersion = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [moduleFilter, setModuleFilter] = useState('');
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/reversals', { params: { from, to } });
      setRows(r.data || []);
      setPage(0);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load reversals')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadVersion]);

  const filtered = useMemo(() => moduleFilter
    ? rows.filter(r => r.source_module === moduleFilter)
    : rows, [rows, moduleFilter]);
  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  // Module options from loaded rows for the dropdown
  const moduleOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const m = r.source_module || '—';
      map.set(m, (map.get(m) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const totalReversed = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      <FilterPanel icon={<HistoryIcon color="primary" />} title="Filters" loading={loading}
        applyLabel="Refresh"
        onApply={load}
        badge={filtered.length > 0 && (
          <Chip size="small" label={`${filtered.length}`}
            color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        )}>
        <DatePresetChips from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        <Stack direction="row" spacing={1}>
          <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        <TextField select fullWidth size="small" label="Source module"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          helperText={moduleOptions.length === 0 ? '—' : `${moduleOptions.length} module${moduleOptions.length === 1 ? '' : 's'} in range`}>
          <MenuItem value=""><em>All modules</em></MenuItem>
          {moduleOptions.map(([m, n]) => (
            <MenuItem key={m} value={m}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Box sx={{ flex: 1 }}>{m}</Box>
                <Chip size="small" label={n}
                  sx={{ height: 18, fontSize: 10, fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }} />
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        {filtered.length > 0 && (
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Reversed in range
            </Typography>
            <Typography fontWeight={800} color="error.main" sx={{ ...TNUM, fontSize: 16 }}>
              GHS {fmtMoney(totalReversed)}
            </Typography>
          </Box>
        )}
      </FilterPanel>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                <TableCell>Reversed At</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Student / Payer</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Reversed By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No reversals match the current filters.</TableCell></TableRow>}
              {!loading && paged.map(r => (
                <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.7, ...TNUM } }}>
                  <TableCell>{fmtDateTime(r.reversed_at)}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.source_module} variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                  </TableCell>
                  <TableCell sx={TNUM}>{r.student_id || '—'}</TableCell>
                  <TableCell>{r.account_name || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                    GHS {fmtMoney(r.amount)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Tooltip title={r.reason}><span>{r.reason}</span></Tooltip>
                  </TableCell>
                  <TableCell>{r.reversed_by_name || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length}
          page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:" showFirstButton showLastButton />
      </Card>
    </Box>
  );
}

// ── 6. Audit Trail ───────────────────────────────────────────
function AuditTrailView({ reloadVersion = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [target, setTarget] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/audit', { params: { from, to, target_type: target } });
      setRows(r.data || []);
      setPage(0);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load audit log')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadVersion]);

  // Distinct action options derived from the loaded slice
  const actionOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const a = r.action || '—';
      map.set(a, (map.get(a) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (q) {
        const blob = `${r.user_name || ''} ${r.action || ''} ${r.description || ''} ${r.target_type || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [rows, actionFilter, search]);
  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      <FilterPanel icon={<AuditIcon color="primary" />} title="Audit filters" loading={loading}
        applyLabel="Apply"
        onApply={load}
        badge={filtered.length > 0 && (
          <Chip size="small" label={`${filtered.length}`} color="primary" variant="outlined"
            sx={{ fontWeight: 700 }} />
        )}>
        <TextField fullWidth size="small" placeholder="Search user / action / text"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <DatePresetChips from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        <Stack direction="row" spacing={1}>
          <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        <TextField select fullWidth size="small" label="Target type" value={target}
          onChange={(e) => setTarget(e.target.value)}>
          <MenuItem value="">All targets</MenuItem>
          {['payment', 'user', 'role', 'student', 'collector_collection'].map(t => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <TextField select fullWidth size="small" label="Action"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          helperText={actionOptions.length === 0 ? '—' : `${actionOptions.length} action${actionOptions.length === 1 ? '' : 's'} in range`}>
          <MenuItem value=""><em>All actions</em></MenuItem>
          {actionOptions.map(([a, n]) => (
            <MenuItem key={a} value={a}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Box sx={{ flex: 1 }}>{a}</Box>
                <Chip size="small" label={n}
                  sx={{ height: 18, fontSize: 10, fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }} />
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>No audit entries match the current filters.</TableCell></TableRow>}
              {!loading && paged.map(r => (
                <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.7 } }}>
                  <TableCell sx={TNUM}>{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell>{r.user_name || `#${r.user_id}`}</TableCell>
                  <TableCell><Chip size="small" label={r.action} sx={{ fontWeight: 700, height: 20 }} /></TableCell>
                  <TableCell sx={TNUM}>{r.target_type ? `${r.target_type}#${r.target_id}` : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Tooltip title={r.description || ''}><span>{r.description}</span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length}
          page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:" showFirstButton showLastButton />
      </Card>
    </Box>
  );
}

// ── Reusable: list of users who can approve payment.reverse ────
// Renders a compact strip of chips so both the requester and the
// approver UI know exactly who is qualified to act on a request.
function ApproversBar({ approvers, dense = false }) {
  if (!approvers || approvers.length === 0) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
        No users currently hold the <b>Approve Payment Reversals</b> right. A
        super administrator must grant it via Roles → Approver Rights before
        any reversal can be approved.
      </Alert>
    );
  }
  const supers   = approvers.filter(a => a.is_super);
  const granted  = approvers.filter(a => !a.is_super);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary"
        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, mb: 0.6 }}>
        Eligible approvers · {approvers.length}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.6} rowGap={0.6}>
        {supers.map(a => (
          <Tooltip key={a.id} title={`Super administrator · ${a.email || ''}`}>
            <Chip size={dense ? 'small' : 'medium'}
              color="secondary"
              avatar={<Avatar sx={{ fontSize: 10, fontWeight: 800 }}>{(a.name?.[0] || '?').toUpperCase()}</Avatar>}
              label={a.name}
              sx={{ fontWeight: 800, height: dense ? 22 : 28 }} />
          </Tooltip>
        ))}
        {granted.map(a => (
          <Tooltip key={a.id} title={`${a.role_label || 'no role'} · ${a.email || ''}`}>
            <Chip size={dense ? 'small' : 'medium'}
              color="primary" variant="outlined"
              avatar={<Avatar sx={{ fontSize: 10, fontWeight: 800 }}>{(a.name?.[0] || '?').toUpperCase()}</Avatar>}
              label={a.name}
              sx={{ fontWeight: 700, height: dense ? 22 : 28 }} />
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
}

// ── Dialog: submit a reversal request ────────────────────────
// Replaces the swal.prompt-only flow with a richer popup that also
// reveals who will see and approve this request before it posts.
function ReversalRequestDialog({ open, onClose, target, approvers, onSubmit }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);

  const submit = async () => {
    const v = reason.trim();
    if (v.length < 5) { setError('Reason must be at least 5 characters'); return; }
    setSubmitting(true); setError('');
    try { await onSubmit(v); }
    catch (e) { setError(e?.response?.data?.message || e.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (!target) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.warning.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 42, height: 42 }}>
          <ReverseIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Request reversal
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {target.receipt_no || target.transaction_id} · GHS {fmtMoney(target.amount || target.credit)}
          </Typography>
        </Box>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {(target.payment_scope === 'FEE' || target.source_module === 'FEE') && target.account && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
            <b>Only the {target.account} line will be reversed</b> — sibling
            accounts on the same receipt (if any) stay POSTED.
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Receipt
              </Typography>
              <Typography fontWeight={800} sx={{ ...TNUM, fontFamily: 'monospace' }}>
                {target.receipt_no || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Amount being reversed
              </Typography>
              <Typography fontWeight={800} color="error.main" sx={TNUM}>
                GHS {fmtMoney(target.amount || target.credit)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Payer / Student
              </Typography>
              <Typography fontWeight={700}>{target.payer_name || target.student_id || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Account / Type
              </Typography>
              <Typography fontWeight={700}>{target.account || target.payment_type || '—'}</Typography>
            </Box>
          </Box>
        </Card>

        <TextField fullWidth multiline rows={3} autoFocus
          label="Reason for reversal"
          value={reason} onChange={(e) => setReason(e.target.value)}
          helperText="Required · at least 5 characters. The approver sees this verbatim."
          sx={{ mb: 2, bgcolor: '#fff' }} />

        <ApproversBar approvers={approvers} dense />

        <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
          {approvers?.some(a => a.id === window.__currentUserId)
            ? 'You hold approval rights — submitting will reverse immediately.'
            : 'Your request will sit as PENDING until one of the people above approves it. Super administrators can also approve.'}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" color="error"
          onClick={submit} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ReverseIcon />}
          sx={{ fontWeight: 800, minWidth: 180, textTransform: 'none' }}>
          {submitting ? 'Submitting…' : 'Submit reversal request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Dialog: approver acts on a pending request ───────────────
// A bigger, friendlier popup for approvers — full transaction context plus
// approve/reject controls. Replaces the swal.confirm flow.
function ApproveReversalDialog({ open, onClose, request, onApprove, onReject, canReverse }) {
  const [mode, setMode] = useState('approve');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setMode('approve'); setReason(''); setError(''); } }, [open]);

  const submit = async () => {
    if (mode === 'reject' && reason.trim().length < 5) {
      setError('Rejection reason must be at least 5 characters'); return;
    }
    setSubmitting(true); setError('');
    try {
      if (mode === 'approve') await onApprove(request);
      else                    await onReject(request, reason.trim());
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (!request) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => mode === 'approve'
          ? `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.dark} 100%)`
          : `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 42, height: 42 }}>
          {mode === 'approve' ? <CheckIcon /> : <RejectIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {mode === 'approve' ? 'Approve reversal' : 'Reject reversal'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {request.receipt_no || request.original_transaction_id} · GHS {fmtMoney(request.amount)}
          </Typography>
        </Box>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <ToggleButtonGroup exclusive size="small" fullWidth value={mode}
          onChange={(_, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="approve" sx={{ textTransform: 'none', fontWeight: 800 }}>
            <CheckIcon fontSize="small" sx={{ mr: 0.6 }} /> Approve & reverse
          </ToggleButton>
          <ToggleButton value="reject" sx={{ textTransform: 'none', fontWeight: 800 }}>
            <RejectIcon fontSize="small" sx={{ mr: 0.6 }} /> Reject
          </ToggleButton>
        </ToggleButtonGroup>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Module
              </Typography>
              <Chip size="small" label={request.source_module} variant="outlined"
                sx={{ fontWeight: 700, mt: 0.3 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Receipt
              </Typography>
              <Typography fontWeight={800} sx={{ ...TNUM, fontFamily: 'monospace' }}>
                {request.receipt_no || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Student
              </Typography>
              <Typography fontWeight={700}>
                {request.student_name || request.student_id || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Account
              </Typography>
              <Typography fontWeight={700}>{request.account_name || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Amount
              </Typography>
              <Typography fontWeight={800} color="error.main" sx={TNUM}>
                GHS {fmtMoney(request.amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
                Requested by
              </Typography>
              <Typography fontWeight={700}>{request.requested_by_name || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {fmtDateTime(request.requested_at)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9, display: 'block' }}>
              Reason
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.3 }}>
              "{request.reason}"
            </Typography>
          </Box>
        </Card>

        {mode === 'approve' ? (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
            <b>This will run the back-out across day, cash_book, cash_column, general_ledger</b>
            {request.source_module === 'FEE_PAYMENT' &&
              ' and restore the student\'s fee account balance (t_paid + payment_detail)'}.
            The action is logged in the audit trail.
          </Alert>
        ) : (
          <TextField fullWidth multiline rows={3} sx={{ mt: 2, bgcolor: '#fff' }}
            label="Reason for rejection" autoFocus
            value={reason} onChange={(e) => setReason(e.target.value)}
            helperText="Visible to the requester. At least 5 characters." />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" color={mode === 'approve' ? 'success' : 'error'}
          onClick={submit} disabled={submitting || !canReverse}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" />
            : (mode === 'approve' ? <CheckIcon /> : <RejectIcon />)}
          sx={{ fontWeight: 800, minWidth: 180, textTransform: 'none' }}>
          {submitting ? 'Working…'
            : (mode === 'approve' ? 'Approve & reverse' : 'Reject request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── 7. Pending Reversals ──────────────────────────────────────
// Approver inbox: list every PENDING reversal request and let approvers
// review, approve (which actually posts the back-out via ReversalService)
// or reject. Mirrors the Adjust Balances approval queue.
function PendingReversalsView({ canReverse, approvers = [], reloadVersion = 0, onReversed }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusF, setStatusF] = useState('PENDING');
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [search, setSearch] = useState('');
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(25);
  // Selected request opens the rich approve/reject dialog
  const [pickedReq, setPickedReq] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/ledgers/reversal-requests', {
        params: { status: statusF, from, to },
      });
      setRows(r.data || []);
      setPage(0);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load reversal requests')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadVersion]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.receipt_no || '').toLowerCase().includes(q) ||
      (r.student_name || '').toLowerCase().includes(q) ||
      (r.student_id || '').toLowerCase().includes(q) ||
      (r.requested_by_name || '').toLowerCase().includes(q) ||
      (r.account_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);
  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  const totals = useMemo(() => {
    let pending = 0, value = 0;
    for (const r of rows) {
      if (r.status === 'PENDING') {
        pending++;
        value += Number(r.amount || 0);
      }
    }
    return { pending, value };
  }, [rows]);

  // Both Approve and Reject open the rich dialog; the dialog posts and
  // closes itself, then we reload.
  const openApproveDialog = (req) => setPickedReq(req);
  const closeApproveDialog = () => setPickedReq(null);

  const approveRequest = async (req) => {
    await API.post(`/ledgers/reversal-requests/${req.id}/approve`);
    swal.success(`Reversal of ${req.receipt_no || ''} posted.`, 'Approved & reversed');
    closeApproveDialog();
    // Bumping the global reload makes every other open tab (Student
    // Ledger, Account Ledger, Transaction Search, Reversal Center,
    // History, Audit) refetch — so the just-reversed row flips its
    // status from POSTED to REVERSED everywhere it appears.
    onReversed && onReversed();
    load();
  };
  const rejectRequest = async (req, reason) => {
    await API.post(`/ledgers/reversal-requests/${req.id}/reject`, { reason });
    swal.success('Rejected');
    closeApproveDialog();
    onReversed && onReversed();
    load();
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 2.4fr' },
      gap: 2,
    }}>
      <FilterPanel icon={<PendingIcon color="primary" />} title="Reversal queue"
        loading={loading}
        applyLabel="Refresh"
        onApply={load}
        badge={totals.pending > 0 && (
          <Chip size="small" color="warning" label={`${totals.pending} pending`}
            sx={{ fontWeight: 700 }} />
        )}
        footer={!canReverse ? (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
            You can submit reversal requests but only users with the
            <b> Approve Payment Reversals</b> right (or super admin) can approve them.
          </Alert>
        ) : (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
            Approving runs the back-out across day, cash_book, cash_column,
            general_ledger and (for fee payments) restores the student's account balance.
          </Alert>
        )}>
        <TextField fullWidth size="small" placeholder="Search receipt / student / requester"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <DatePresetChips from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        <Stack direction="row" spacing={1}>
          <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        <TextField select fullWidth size="small" label="Status"
          value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
        {totals.pending > 0 && (
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Pending reversal value
            </Typography>
            <Typography fontWeight={800} color="warning.dark" sx={{ ...TNUM, fontSize: 16 }}>
              GHS {fmtMoney(totals.value)}
            </Typography>
          </Box>
        )}
      </FilterPanel>

      <Box>
        {/* Approvers banner — at the top of the right pane so admins always
            see who is qualified to act on these requests. */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ pb: '14px !important' }}>
            <ApproversBar approvers={approvers} dense />
          </CardContent>
        </Card>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7 } }}>
                <TableCell>Requested</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Student / Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No reversal requests in this view.
                </TableCell></TableRow>
              )}
              {!loading && paged.map(r => {
                const sc = statusChip(r.status);
                const isPending = r.status === 'PENDING';
                return (
                  <TableRow key={r.id} hover sx={{
                    '& td': { fontSize: 12, py: 0.7, ...TNUM },
                    opacity: r.status === 'REJECTED' ? 0.55 : 1,
                  }}>
                    <TableCell>{fmtDateTime(r.requested_at)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.source_module} variant="outlined"
                        sx={{ fontWeight: 700, height: 20 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize={12} fontWeight={600}>
                        {r.student_name || r.student_id || '—'}
                      </Typography>
                      {r.account_name && (
                        <Typography variant="caption" color="text.secondary">{r.account_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                      GHS {fmtMoney(r.amount)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={r.reason || ''}><span>{r.reason}</span></Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize={12} fontWeight={600}>{r.requested_by_name || '—'}</Typography>
                      {r.approved_by_name && (
                        <Typography variant="caption" color="text.secondary">→ {r.approved_by_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={sc.color} label={sc.label} sx={{ fontWeight: 800, height: 20 }} />
                      {r.rejected_reason && (
                        <Tooltip title={r.rejected_reason}>
                          <Typography variant="caption" color="error.main"
                            sx={{ display: 'block', fontStyle: 'italic', fontSize: 10, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.rejected_reason}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isPending ? (
                        <Tooltip title={canReverse ? 'Open review dialog' : 'Need payment.reverse approval'}>
                          <span>
                            <Button size="small" variant="contained" color="primary"
                              disabled={!canReverse}
                              onClick={() => openApproveDialog(r)}
                              sx={{ fontWeight: 800, textTransform: 'none', minWidth: 0, px: 1.6, py: 0.4 }}>
                              Review
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          {fmtDateTime(r.approved_at)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length}
          page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:" showFirstButton showLastButton />
      </Card>
      </Box>

      <ApproveReversalDialog open={!!pickedReq}
        request={pickedReq}
        canReverse={canReverse}
        onClose={closeApproveDialog}
        onApprove={approveRequest}
        onReject={rejectRequest} />
    </Box>
  );
}

// ── Top-level page ────────────────────────────────────────────
export default function LedgersAndReversalsPage() {
  const { user } = useAuth();
  const canReverse = user?.role === 'super_admin' || (user?.approvals || []).includes('payment.reverse');

  const [tab, setTab] = useState('student');

  // Used by ReversalRequestDialog's "you can self-approve" hint.
  if (typeof window !== 'undefined') window.__currentUserId = user?.id;

  // Eligible approvers — fetched once for the page; consumed by both the
  // request dialog (so submitter sees who'll review) and the Pending
  // Reversals tab (so approvers see their peers).
  const [approvers, setApprovers] = useState([]);
  useEffect(() => {
    API.get('/ledgers/reversal-requests/approvers')
      .then(r => setApprovers(r.data || []))
      .catch(() => setApprovers([]));
  }, []);

  // Reversal request dialog state
  const [revOpen, setRevOpen] = useState(false);
  const [revTarget, setRevTarget] = useState(null);

  // Bump after any successful reversal / approval / rejection so every
  // open view (Student Ledger, Account Ledger, Transaction Search,
  // Reversal Center, Pending, History, Audit) reloads its rows. That's
  // how the just-reversed line flips from POSTED → REVERSED in the table
  // without the user manually hitting Apply, and stops a second click on
  // a row that's already been reversed.
  const [reloadVersion, setReloadVersion] = useState(0);
  const bumpReload = () => setReloadVersion(v => v + 1);

  // Receipt dialogs (shared across tabs)
  const [feeOpen, setFeeOpen] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [feeLoad, setFeeLoad] = useState(false);
  const [feeError, setFeeError] = useState('');
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherData, setOtherData] = useState(null);
  const [otherLoad, setOtherLoad] = useState(false);
  const [otherError, setOtherError] = useState('');

  const openReceipt = async (r) => {
    if ((r.payment_scope === 'FEE' || r.source_module === 'FEE') && r.payment_id) {
      setFeeOpen(true); setFeeLoad(true); setFeeData(null); setFeeError('');
      try {
        const det = await API.get(`/payments/${r.payment_id}/receipt`);
        setFeeData(det.data);
      } catch (e) { setFeeError(swal.pickError(e, 'Could not load receipt')); }
      finally { setFeeLoad(false); }
      return;
    }
    if (r.transaction_id && (r.payment_scope || '').startsWith('OTHER')) {
      setOtherOpen(true); setOtherLoad(true); setOtherData(null); setOtherError('');
      try {
        const det = await API.get(`/other-payments/${r.transaction_id}/receipt`);
        setOtherData(det.data);
      } catch (e) { setOtherError(swal.pickError(e, 'Could not load receipt')); }
      finally { setOtherLoad(false); }
      return;
    }
    swal.info(
      `Receipt ${r.receipt_no || ''} · GHS ${fmtMoney(r.amount || r.credit)}\nLegacy receipt — no detailed view available here.`,
      'Legacy receipt'
    );
  };

  const openReverse = (r) => { setRevTarget(r); setRevOpen(true); };

  const submitReversal = async (reason) => {
    const r = revTarget;
    const isFee = r.payment_scope === 'FEE' || r.source_module === 'FEE';
    const targetUrl = isFee
      ? `/ledgers/transactions/fee/${r.payment_id}/reverse`
      : `/ledgers/transactions/other/${r.transaction_id}/reverse`;
    const body = { reason };
    // For fee payments, scope the reversal to ONLY this account's line.
    // The Reversal Center search returns one row per (receipt × account)
    // — using r.account here makes sure reversing PTA never touches the
    // school_fees or other-payment lines that paid on the same receipt.
    if (isFee && r.account) body.account = r.account;
    const res = await API.post(targetUrl, body);
    setRevOpen(false); setRevTarget(null);
    if (res.data?.status === 'APPROVED') {
      const detail = isFee && r.account
        ? `${r.account} on receipt ${r.receipt_no} reversed.`
        : `Receipt ${r.receipt_no} reversed.`;
      swal.success(detail, 'Reversed');
    } else {
      const detail = isFee && r.account
        ? `Your request to reverse ${r.account} (GHS ${fmtMoney(r.amount)}) on receipt ${r.receipt_no} is queued.`
        : `Your reversal request for ${r.receipt_no} has been queued.`;
      swal.success(`${detail} An approver will review it before it posts to the ledger.`, 'Awaiting approval');
    }
    // Tell every open view to refetch so the just-acted-on row flips
    // from POSTED → REVERSED (and disappears from the reversible set).
    bumpReload();
  };

  const tabs = useMemo(() => ([
    { v: 'student',  label: 'Student Ledger',      icon: <PersonIcon fontSize="small" /> },
    { v: 'account',  label: 'Account Ledger',      icon: <AccountIcon fontSize="small" /> },
    { v: 'search',   label: 'Transaction Search',  icon: <SearchIcon fontSize="small" /> },
    { v: 'reverse',  label: 'Reversal Center',     icon: <ReverseIcon fontSize="small" /> },
    { v: 'pending',  label: 'Pending Reversals',   icon: <PendingIcon fontSize="small" /> },
    { v: 'history',  label: 'Reversal History',    icon: <HistoryIcon fontSize="small" /> },
    { v: 'audit',    label: 'Audit Trail',         icon: <AuditIcon fontSize="small" /> },
  ]), []);

  const props = { openReceipt, openReverse, canReverse, reloadVersion };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<LedgersIcon />}
        title="Receipts Ledger & Reversals"
        subtitle="Audit history, transaction tracing and reversal of posted payments"
      />

      <Card sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ px: 1, '& .MuiTab-root': { minHeight: 56, fontWeight: 700, textTransform: 'none', fontSize: 13.5 } }}>
          {tabs.map(t => (
            <Tab key={t.v} value={t.v} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Card>

      {tab === 'student' && <StudentLedgerView {...props} />}
      {tab === 'account' && <AccountLedgerView {...props} />}
      {tab === 'search'  && <TransactionSearchView {...props} />}
      {tab === 'reverse' && <ReversalCenterView {...props} approvers={approvers} />}
      {tab === 'pending' && <PendingReversalsView canReverse={canReverse} approvers={approvers}
        reloadVersion={reloadVersion} onReversed={bumpReload} />}
      {tab === 'history' && <ReversalHistoryView reloadVersion={reloadVersion} />}
      {tab === 'audit'   && <AuditTrailView reloadVersion={reloadVersion} />}

      <ReversalRequestDialog open={revOpen}
        onClose={() => { setRevOpen(false); setRevTarget(null); }}
        target={revTarget}
        approvers={approvers}
        onSubmit={submitReversal} />

      <ReceiptDialog open={feeOpen} onClose={() => setFeeOpen(false)}
        data={feeData} loading={feeLoad} error={feeError} />
      <OtherReceiptDialog open={otherOpen} onClose={() => setOtherOpen(false)}
        data={otherData} loading={otherLoad} error={otherError} />
    </Box>
  );
}
